import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Send, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/format";
import { dueLabel, exerciseFileUrl, submissionFileUrl, gradeLabel } from "@/lib/exercises";
import { FileLinks, SubStatusBadge } from "@/components/exercises/common";

function SubmitForm({ ex, sub, onDone, onCancel }) {
  const [text, setText] = useState(sub?.text || "");
  const [files, setFiles] = useState([]);
  const [keep, setKeep] = useState((sub?.files || []).map((f) => f.id));
  const mut = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("text", text);
      if (sub) fd.append("keep_file_ids", keep.join(","));
      files.forEach((f) => fd.append("files", f));
      return (await api.post(`/exercises/${ex.id}/submit`, fd)).data;
    },
    onSuccess: () => { toast.success(sub ? "Consegna aggiornata" : "Elaborato inviato"); setText(""); setFiles([]); onDone(); },
    onError: (e) => toast.error(
      e.response?.status === 413 ? "File troppo grandi: riduci la dimensione o invia un file alla volta"
        : e.response?.data?.detail || (e.response ? "Errore invio" : "Connessione interrotta durante il caricamento: riprova con una rete stabile"),
      { duration: 8000 }),
  });
  return (
    <div className="space-y-2 pt-2 border-t border-stone-100">
      {ex.is_late_now && !sub && <div className="text-[11px] text-red-700">La scadenza è passata: la consegna sarà segnata "in ritardo".</div>}
      {sub && <div className="text-[11px] text-stone-500">Stai modificando la consegna: puoi farlo fino alla scadenza o finché il docente non la valuta.</div>}
      <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Testo o note per il docente (facoltativo)" data-testid={`submit-text-${ex.id}`}/>
      {sub?.files?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {sub.files.filter((f) => keep.includes(f.id)).map((f) => (
            <span key={f.id} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-stone-100 border border-stone-200">
              {f.filename}
              <button type="button" onClick={() => setKeep(keep.filter((k) => k !== f.id))} data-testid={`remove-sub-file-${f.id}`} className="text-stone-500 hover:text-red-700"><X size={11}/></button>
            </span>
          ))}
        </div>
      )}
      <Input type="file" multiple onChange={(e) => {
        const list = Array.from(e.target.files || []);
        const big = list.find((f) => f.size > 50 * 1024 * 1024);
        if (big) { toast.error(`${big.name}: file troppo grande (max 50 MB)`); e.target.value = ""; return; }
        setFiles(list);
      }} data-testid={`submit-files-${ex.id}`}/>
      <div className="text-[11px] text-stone-400" data-testid={`submit-formats-${ex.id}`}>PDF, foto (anche iPhone), Word, Excel, PowerPoint, Pages, LibreOffice, audio, video, ZIP · max 50 MB per file</div>
      <div className="flex gap-2">
        <Button size="sm" className="bg-[#1C352D] hover:bg-[#264A3E]" disabled={(!text.trim() && !files.length && !keep.length) || mut.isPending} onClick={() => mut.mutate()} data-testid={`submit-btn-${ex.id}`}>
          <Send size={13} className="mr-1.5"/> {mut.isPending ? "Invio…" : sub ? "Salva modifiche" : "Invia elaborato"}
        </Button>
        {onCancel && <Button size="sm" variant="outline" onClick={onCancel} data-testid={`submit-cancel-${ex.id}`}>Annulla</Button>}
      </div>
    </div>
  );
}

export default function StudentExerciseCard({ ex, queryKey, showCourse }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const sub = ex.my_submission;
  const canSubmit = !sub || sub.status === "da_rivedere";
  const canEdit = sub?.status === "consegnato" && !ex.is_late_now;
  const refresh = () => { setEditing(false); qc.invalidateQueries({ queryKey }); };
  return (
    <Card className="p-4 border border-stone-200 rounded-xl shadow-none space-y-2" data-testid={`student-exercise-${ex.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          {showCourse && <div className="text-[10px] uppercase tracking-widest text-[#E2725B] font-semibold">{ex.is_seminar ? "Seminario · " : ""}{ex.course_name}</div>}
          <div className="font-medium">{ex.title}</div>
          <div className={`text-[11px] ${ex.is_late_now && !sub ? "text-red-700" : "text-stone-500"}`}>{dueLabel(ex.due_date)}</div>
        </div>
        {sub ? <SubStatusBadge status={sub.status} late={sub.late}/> : <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-stone-50 text-stone-600 border-stone-200">Da consegnare</span>}
      </div>
      {ex.instructions && <p className="text-sm text-stone-700 whitespace-pre-line">{ex.instructions}</p>}
      <FileLinks files={ex.attachments} urlFor={(fid) => exerciseFileUrl(ex.id, fid)} testPrefix="student-exercise-file"/>
      {sub && (
        <div className="p-3 rounded-lg bg-stone-50 text-xs space-y-1">
          <div className="text-stone-500">La tua consegna · {fmtDateTime(sub.submitted_at)}{sub.edited_at ? ` · modificata ${fmtDateTime(sub.edited_at)}` : ""}{sub.uploaded_by_name ? ` · caricata dalla segreteria` : ""}</div>
          {sub.text && <div className="whitespace-pre-line text-stone-700">{sub.text}</div>}
          <FileLinks files={sub.files} urlFor={(fid) => submissionFileUrl(sub.id, fid)} testPrefix="my-submission-file"/>
          {gradeLabel(sub) && <div className="font-semibold text-[#1C352D]" data-testid={`my-grade-${ex.id}`}>Voto: {gradeLabel(sub)}</div>}
          {sub.feedback && <div className="text-stone-700">Commento del docente: {sub.feedback}</div>}
        </div>
      )}
      {canSubmit && !submitting && (
        <Button size="sm" className="bg-[#1C352D] hover:bg-[#264A3E]" onClick={() => setSubmitting(true)} data-testid={`open-submit-${ex.id}`}>
          <Send size={13} className="mr-1.5"/> {sub ? "Consegna di nuovo" : "Consegna"}
        </Button>
      )}
      {canSubmit && submitting && <SubmitForm ex={ex} onDone={() => { setSubmitting(false); refresh(); }} onCancel={() => setSubmitting(false)}/>}
      {canEdit && !editing && (
        <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid={`edit-submission-${ex.id}`}>
          <Pencil size={13} className="mr-1.5"/> Modifica consegna
        </Button>
      )}
      {canEdit && editing && <SubmitForm ex={ex} sub={sub} onDone={refresh} onCancel={() => setEditing(false)}/>}
    </Card>
  );
}
